import { useEffect, useMemo, useState } from 'react';
import {
  fetchVendorStats,
  fetchVendorAnnonceIds,
  getPeriodRange,
  EMPTY_VENDOR_STATS,
  PERIOD_LABELS,
  type VendorStats,
  type StatsPeriod,
  type StatBlock,
} from '@/lib/vendorStats';
import AiChatStatsCard from '@/components/feature/marketplace/AiChatStatsCard';
import VendorNotificationsFeed from './VendorNotificationsFeed';

interface VendorStatsTabProps {
  vendorId: number;
  annonceIds: number[];
  currency: string;
}

const PERIODS: StatsPeriod[] = ['7d', '30d', 'month'];

/** Évolution en % entre la période et la période précédente. */
function trendOf(block: StatBlock): number | null {
  if (block.previous <= 0) return null;
  return Math.round(((block.value - block.previous) / block.previous) * 100);
}

function Trend({ block }: { block: StatBlock }) {
  const trend = trendOf(block);
  if (trend == null) {
    return block.value > 0 ? (
      <span className="inline-flex items-center gap-1 text-[11px] text-accent-700">
        <i className="ri-arrow-up-line"></i>
        Nouveau
      </span>
    ) : (
      <span className="text-[11px] text-foreground-400">— vs période préc.</span>
    );
  }
  const up = trend >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${up ? 'text-accent-700' : 'text-red-500'}`}>
      <i className={up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
      {up ? '+' : ''}{trend}% <span className="text-foreground-400 font-normal">vs période préc.</span>
    </span>
  );
}

function StatCard({
  icon,
  label,
  value,
  block,
  tone,
}: {
  icon: string;
  label: string;
  value: string;
  block?: StatBlock;
  tone: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center mb-3`}>
        <i className={`${icon} text-lg`}></i>
      </div>
      <div className="text-2xl font-bold font-heading text-foreground-950 tabular-nums">{value}</div>
      <div className="text-xs text-foreground-500 mt-0.5">{label}</div>
      {block && (
        <div className="mt-1.5">
          <Trend block={block} />
        </div>
      )}
    </div>
  );
}

/**
 * Statistiques du vendeur : vitrine, vues des annonces, commandes,
 * revenus et prises de contact — filtrables par période (7j / 30j / mois).
 */
export default function VendorStatsTab({ vendorId, annonceIds, currency }: VendorStatsTabProps) {
  const [stats, setStats] = useState<VendorStats>(EMPTY_VENDOR_STATS);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<StatsPeriod>('30d');

  const range = useMemo(() => getPeriodRange(period), [period]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const ids = annonceIds.length > 0 ? annonceIds : await fetchVendorAnnonceIds(vendorId);
      const result = await fetchVendorStats(vendorId, ids, period);
      if (!active) return;
      setStats(result);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [vendorId, annonceIds, period]);

  const money = (n: number) => `${Math.round(n).toLocaleString()} ${currency}`;

  const rangeLabel = useMemo(() => {
    const fmt = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return `du ${fmt(range.from)} au ${fmt(range.to)}`;
  }, [range]);

  return (
    <div>
      {/* Filtre par période */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                period === p ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-foreground-400 flex items-center gap-1.5">
          <i className="ri-calendar-line"></i>
          {rangeLabel}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
            <StatCard
              icon="ri-store-2-line"
              label="Visites de la vitrine"
              value={stats.shopVisits.value.toLocaleString()}
              block={stats.shopVisits}
              tone="bg-primary-100 text-primary-700"
            />
            <StatCard
              icon="ri-eye-line"
              label="Vues de vos annonces"
              value={stats.contentViews.value.toLocaleString()}
              block={stats.contentViews}
              tone="bg-accent-100 text-accent-700"
            />
            <StatCard
              icon="ri-shopping-cart-2-line"
              label="Commandes reçues"
              value={stats.orders.value.toLocaleString()}
              block={stats.orders}
              tone="bg-secondary-100 text-secondary-900"
            />
            <StatCard
              icon="ri-money-dollar-circle-line"
              label="Revenus sur la période"
              value={money(stats.revenue.value)}
              block={stats.revenue}
              tone="bg-primary-100 text-primary-700"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-whatsapp-line text-lg"></i>
              </div>
              <div>
                <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.clicks.toLocaleString()}</div>
                <div className="text-xs text-foreground-500">Clics / contacts sur vos annonces (total)</div>
              </div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-line text-lg"></i>
              </div>
              <div>
                <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.annonces.toLocaleString()}</div>
                <div className="text-xs text-foreground-500">Annonces publiées (total)</div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-foreground-400 flex items-start gap-2">
            <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
            <span>Les statistiques se remplissent au fur et à mesure des visites de votre vitrine et de vos annonces sur la marketplace. Chaque période est comparée à la période précédente équivalente.</span>
          </p>

          {/* Notifications : commandes (dont IA), réservations et vues de la boutique */}
          <div className="mt-6">
            <VendorNotificationsFeed vendorId={vendorId} title="Notifications & activité" />
          </div>

          {/* Assistant IA : recherche, consultation et ajout au panier des clients */}
          <div className="mt-6">
            <AiChatStatsCard merchantIds={[vendorId]} title="Assistant IA (chat client)" showRecent />
          </div>
        </>
      )}
    </div>
  );
}